import { ReactNode } from "react"
import Container from "@/components/Container"
import { supportEmail } from "@/constants"
import DefaultLayout from "@/layouts/DefaultLayout"

const TermsPage = () => (
  <section>
    <Container>
      <div className="prose prose-stone sm:prose-lg mx-auto my-16 sm:my-20">
        <h1>Terms &amp; Conditions</h1>
        <p>Last updated on 2024-07-10.</p>
        <p>
          Please read these Terms of Service (these “Terms”) and our Privacy
          Policy carefully because they govern your use of the website located
          at chessdrop.com, any associated website and APIs (collectively, the
          “Site”), together with any applications, including mobile
          applications, and the services accessible via the Site, including
          ChessDrop (as defined below) any Apps (together with the Site and
          Apps, the “Services”) offered by Lakpa SLU (the “Company”, “we”, “our”
          or “us”). These Terms refer to any individual or entity using the
          Services as “you” or “your”.
        </p>
        <p>
          IMPORTANT NOTICE REGARDING ARBITRATION: THESE TERMS CONTAIN ADDITIONAL
          PROVISIONS APPLICABLE ONLY TO YOU. WHEN YOU AGREE TO THESE TERMS YOU
          ARE AGREEING (WITH LIMITED EXCEPTION) TO RESOLVE ANY DISPUTE BETWEEN
          YOU AND LAKPA SLU THROUGH BINDING, INDIVIDUAL ARBITRATION RATHER THAN
          IN COURT. PLEASE REVIEW CAREFULLY SECTION 27 BELOW, “HOW CAN WE
          RESOLVE DISPUTES?”, FOR DETAILS REGARDING ARBITRATION.
        </p>
        <h2>1. What is the scope of the Terms?</h2>
        <p>
          A. By accessing or using the Services, you agree that you have read,
          understood, and accept all of the terms and conditions contained in
          these Terms including the Cookie Policy and the Privacy Policy, as
          each may be amended from time to time, which are hereby incorporated
          herein by reference. If you do not agree with these Terms, you must
          not access or use the Services.
        </p>
        <p>
          B. From time to time, we may change these Terms. If we do this, then
          we will publish those changes on the Site. It is important that you
          review these Terms each time you use or access the Services. If you
          continue to use or access the Services after we have posted updated
          Terms it means that you accept and agree to the changes and you will
          be bound by those new terms the next time you use the Services. If you
          do not agree to any changes, you must not use or access the Services.
          Because the Services are evolving over time we may change or
          discontinue all or any part of the Services, at any time and without
          notice, at our sole discretion.
        </p>
        <h2>2. What do some of the capitalised terms mean in these Terms?</h2>
        <p>
          A. “ChessDrop” means an online service to record, store, access and
          share chess games.
        </p>
        <p>
          B. “Force Majeure Event” means any event, circumstance or cause beyond
          our reasonable control, which prevents, hinders or delays the
          provision of the Services or makes their provision impossible or
          onerous, including, without limitation: (a) acts of God, flood, storm,
          drought, earthquake or other natural disaster; (b) epidemics or
          pandemics (for the avoidance of doubt, including the 2020 Coronavirus
          Pandemic); terrorist attacks, hacking or cyber threats, civil
          commotion or riots, war, threat of or preparation for war, armed
          conflict, imposition of sanctions, embargo, or breaking off of
          diplomatic relations; or (d) equipment or software malfunction or bug,
          as well as hacks, phishing attacks, distributed denials of service or
          any other security attacks.
        </p>
        <h2>3. What are the Services of ChessDrop?</h2>
        <p>ChessDrop is a software platform that enables its users to:</p>
        <p>A. Create and manage an account with the Service.</p>
        <p>
          B. Record, store, edit, annotate, analyze, manage, share, import, and
          export chess games.
        </p>
        <h2>4. Are you eligible to use the Services?</h2>
        <p>
          A. THE SERVICES ARE NOT AVAILABLE TO PERSONS UNDER THE AGE OF 13, or
          in certain jurisdictions, under 16 (“Minors”) and we do not knowingly
          collect information from Minors. If you are between 13 (or 16 in
          certain jurisdictions) and 18, you must have permission from your
          legal guardian before you are permitted to use the Services.
        </p>
        <p>
          B. You must have the full right, power, and authority to enter into
          and comply with these Terms on behalf of yourself and any company or
          legal entity for which you may access or use the Services.
          Furthermore, the Services are not available to any User previously
          removed from the Services by us.
        </p>
        <p>
          C. The Services are operated out of Andorra. The Services may not be
          available or appropriate for use in other jurisdictions. You must not
          use the Services if your use of them would be illegal or otherwise
          violate any law you are subject to. We are not liable for your
          compliance with such laws.
        </p>
        <p>
          D. You must not be, and will not be, located in any jurisdiction that
          is the subject of an embargo by the United States, the United Kingdom,
          the European Union or Switzerland and you are not listed on any list
          of prohibited or restricted parties by those foregoing. By using the
          Services, or any part thereof, you represent and warrant that: (i) you
          are not a citizen, resident, or member of any jurisdiction or group
          that is subject to economic sanctions by the United States, the United
          Kingdom, the European Union or Switzerland or any other relevant
          jurisdiction; and (ii) you do not appear on HMT Sanctions List, the
          U.S. Treasury Department’s Office of Foreign Asset Control’s sanctions
          lists, the U.S. commerce department’s consolidated screening list, the
          EU consolidated list of persons, groups or entities subject to EU
          Financial Sanctions, nor do you act on behalf of a person sanctioned
          thereunder.
        </p>
        <p>
          E. You represent and warrant that any information you provide via the
          Services is accurate and complete. You accept and acknowledge that the
          Company is not responsible for any errors or omissions that you make
          in connection with any transaction initiated via the Services, for
          instance, if you mistype a recipient’s username or otherwise provide
          incorrect information. You agree to review your transactions carefully
          before completing them via the Services.
        </p>
        <h2>5. How do you access the services?</h2>
        <p>
          A. If you wish to access the Services, you must create a ChessDrop
          account through the Services.
        </p>
        <p>
          B. You hereby accept and acknowledge that you will take responsibility
          for all activities that occur under your ChessDrop account and accept
          all risks of any authorized or unauthorized access to your ChessDrop
          account, to the maximum extent permitted by law.
        </p>
        <p>
          C. We may, in our sole discretion, with or without prior notice and at
          any time, suspend, modify or terminate, temporarily or permanently,
          all or any portion of the Services, with or without reason, including,
          without limitation, for any of the following reasons: (i) you create
          risk or possible legal exposure for us; (ii) our provision of the
          Services, or any part thereof, to you is no longer commercially
          viable; and (iii) if you breach these Terms.
        </p>
        <h2>6. What licenses and access do we grant to you?</h2>
        <p>
          A. All intellectual property rights in the ChessDrop and the Services
          throughout the world belong to us and/or our licensors. Nothing in
          these terms gives you any rights in respect of any intellectual
          property owned by us or our licensors and you acknowledge that you do
          not acquire any ownership rights by accessing or using the Services,
          or any portion thereof.
        </p>
        <p>
          B. The Services may contain code, commonly referred to as open source
          software, which is distributed under open source licence terms,
          including terms which allow the free distribution and modification of
          the relevant software’s source code and/or which require all
          distributors to make such source code freely available upon request,
          including any contributions or modifications made by such distributor
          (“Open Source Software”). To the extent that the Services contain any
          Open Source Software, that element only is licensed to you under the
          relevant licence terms of the applicable third party licensor (“Open
          Source Licence Terms”) and not under these Terms, and you accept and
          agree to be bound by such Open Source Licence Terms.
        </p>
        <h2>
          7. What can you expect from the Services and can we make changes to
          them?
        </h2>
        <p>
          A. Except as set out in these Terms, we do not warrant, represent or
          guarantee that the Services will be accurate, complete, correct,
          reliable, integral, fit for purpose, secure or free from weaknesses,
          vulnerabilities or bugs.
        </p>
        <p>
          B. You understand and accept that you use the Services at your own
          risk.
        </p>
        <p>
          C. To the fullest extent permitted by law, we provide the Services to
          you “as is” and “as available” without any warranty, representation or
          assurance (whether express or implied) in relation to merchantability,
          fitness for a particular purpose, availability, security, title or
          non-infringement.
        </p>
        <p>
          D. We reserve the right to change the format and features of the
          Services by making any updates to Services or, where your device
          settings permit it, by automatic delivery of updates.
        </p>
        <p>
          E. We may cease to provide and/or update content to the Services, with
          or without notice to you, if it improves the Services we provide to
          you, or we need to do so for security, legal or any other reasons.
        </p>
        <h2>
          8. What about third-party risk and the terms of third party providers?
        </h2>
        <p>
          B. The Services rely in part on third party and open-source software,
          and the continued development and support by third parties. There is
          no assurance or guarantee that those third parties will maintain their
          support of their software or that open source software will continue
          to be maintained. This may have a material adverse effect on the
          Services.
        </p>
        <p>
          C. In connection with your use of the Services, you may be offered or
          made aware of services, content, features, products, Third Party
          Applications, offers and promotions providers by third parties
          (collectively, “Third Party Services”). Our inclusion or promotion of
          Third Party Services does not reflect a sponsorship, endorsement,
          approval, or verification by us. Your use, access, and exchange of
          data with any Third Party Services is at your own risk and subject to
          terms and conditions between you and the provider of the Third Party
          Services. We do not warrant any Third Party Services in any way.
        </p>
        <p>F. For more information please also refer to our Privacy Policy.</p>
        <h2>9. What do you agree, warrant and represent?</h2>
        <p>You agree not to do any of the following:</p>
        <p>
          A. Use, display, mirror or frame the Services or any individual
          element within the Services, the Company’s name, any Company
          trademark, logo or other proprietary information, or the layout and
          design of any page or form contained on a page, without Company’s
          express written consent;
        </p>
        <p>
          B. Access, tamper with, or use non-public areas of the Services,
          Company’s computer systems, or the technical delivery systems of
          Company’s providers;
        </p>
        <p>
          C. Attempt to probe, scan or test the vulnerability of the Services,
          or any Company system or network or breach any security or
          authentication measures;
        </p>
        <p>
          D. Avoid, bypass, remove, deactivate, impair, descramble or otherwise
          circumvent any technological measure implemented by Company or any of
          Company’s providers or any other third party (including another user)
          to protect the Services;
        </p>
        <p>
          E. Attempt to access or search the Services or download any
          information from the Services, through the use of any engine,
          software, tool, agent, device or mechanism (including spiders, robots,
          crawlers, data mining tools or the like) other than the software
          and/or search agents provided by Company or available through the
          Services;
        </p>
        <p>
          F. Use or access the Services for the purpose of sending any
          unsolicited or unauthorized advertising, promotional materials, email,
          junk mail, spam, chain letters or other form of solicitation;
        </p>
        <p>
          G. Use any meta tags or other hidden text or metadata utilizing a
          Company trademark, logo URL or product name without Company’s express
          written consent;
        </p>
        <p>
          H. Use the Services, or any portion thereof, for any commercial
          purpose or for the benefit of any third party or in any manner not
          permitted by these Terms;
        </p>
        <p>
          I. Forge any TCP/IP packet header or any part of the header
          information in any email or newsgroup posting, or in any way use the
          Services to send altered, deceptive or false source-identifying
          information;
        </p>
        <p>
          J. Attempt to decipher, decompile, disassemble or reverse engineer any
          of the software used to provide the Services;
        </p>
        <p>
          K. Interfere with, or attempt to interfere with, the access of any
          user, host or network, including, without limitation, sending a virus,
          overloading, flooding, spamming, or mail-bombing the Services;
        </p>
        <p>
          L. Collect or store any personally identifiable information from the
          Services from other users of the Services without their express
          permission;
        </p>
        <p>M. Impersonate or misrepresent any person or entity;</p>
        <p>
          N. Use the Services for activities that are unlawful or fraudulent or
          have such purpose or effect or otherwise support any activities that
          breach applicable local, national or international law or regulations;
        </p>
        <p>
          P. Use the Services for activities that are unlawful or fraudulent or
          have such purpose or effect or otherwise support any activities that
          breach applicable local, national or international law or regulations;
        </p>
        <p>
          Q. Use the Services or interact with the Services in a manner that
          violates any law or regulation, including, without limitation, any
          applicable export control laws; or
        </p>
        <p>
          R. Encourage or enable any other individual to do any of the
          foregoing. Although we are not obligated to monitor access to or use
          of the Services, we have the right to do so for the purpose of
          operating the Services, to ensure compliance with these Terms and to
          comply with applicable law or other legal requirements. We have the
          right to investigate violations of these Terms or conduct that affects
          the Services. We may also consult and cooperate with law enforcement
          authorities to prosecute users who violate the law.
        </p>
        <h2>
          10. What if you breach these Terms or we incur damages in connection
          with your use of the Services?
        </h2>
        <p>
          A. You will indemnify and hold the Company and its officers,
          directors, employees and agents, harmless from and against any claims,
          disputes, demands, liabilities, damages, losses, and costs and
          expenses, including, without limitation, reasonable legal and
          accounting fees arising out of or in any way connected with (a) your
          access to or use of the Services, or (b) your violation of these
          Terms.
        </p>
        <p>
          B. You agree that you will be liable for any losses sustained by us as
          a result of your breach of these Terms and will compensate us in full
          for any such losses.
        </p>
        <p>
          C. We reserve the right, at our own expense, to assume the exclusive
          defense and control of any matter otherwise subject to indemnification
          by you pursuant to paragraph 1 of this sectionand, in such case, you
          agree to cooperate with us in the defence of such matter.
        </p>
        <p>
          D. The indemnity set out in this clause is in addition to, and not in
          lieu of, any other remedies that may be available to us under
          applicable law.
        </p>
        <h2>11. What about our liability to you?</h2>
        <p>
          A. TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER THE COMPANY NOR ITS
          SERVICE PROVIDERS INVOLVED IN CREATING, PRODUCING, OR DELIVERING THE
          SERVICES WILL BE LIABLE FOR ANY INCIDENTAL, SPECIAL, EXEMPLARY OR
          CONSEQUENTIAL DAMAGES, OR DAMAGES FOR LOST PROFITS, LOST REVENUES,
          LOST SAVINGS, LOST BUSINESS OPPORTUNITY, LOSS OF DATA OR GOODWILL,
          SERVICE INTERRUPTION, COMPUTER DAMAGE OR SYSTEM FAILURE OR THE COST OF
          SUBSTITUTE SERVICES OF ANY KIND ARISING OUT OF OR IN CONNECTION WITH
          THESE TERMS OR FROM THE USE OF OR INABILITY TO USE THE SERVICES,
          WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE),
          PRODUCT LIABILITY OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT CLIENT
          OR ITS SERVICE PROVIDERS HAS BEEN INFORMED OF THE POSSIBILITY OF SUCH
          DAMAGE, EVEN IF A LIMITED REMEDY SET FORTH HEREIN IS FOUND TO HAVE
          FAILED OF ITS ESSENTIAL PURPOSE.
        </p>
        <p>
          B. TO THE MAXIMUM EXTENT PERMITTED BY THE LAW OF THE APPLICABLE
          JURISDICTION, IN NO EVENT WILL CLIENT’S TOTAL LIABILITY ARISING OUT OF
          OR IN CONNECTION WITH THESE TERMS OR FROM THE USE OF OR INABILITY TO
          USE THE SERVICES EXCEED THE AMOUNTS YOU HAVE PAID OR ARE PAYABLE BY
          YOU TO CLIENT FOR USE OF THE SERVICES OR ONE HUNDRED DOLLARS ($100),
          IF YOU HAVE NOT HAD ANY PAYMENT OBLIGATIONS TO CLIENT, AS APPLICABLE.
        </p>
        <p>
          C. THE EXCLUSIONS AND LIMITATIONS OF DAMAGES SET FORTH ABOVE ARE
          FUNDAMENTAL ELEMENTS OF THE BASIS OF THE BARGAIN BETWEEN THE COMPANY
          AND YOU.
        </p>
        <h2>12. What about viruses, bugs and security vulnerabilities?</h2>
        <p>
          A. We do not guarantee that the Services will be secure or free from
          bugs, security vulnerabilities or viruses.
        </p>
        <p>
          B. You are responsible for configuring your information technology and
          computer programmes to access the Services and to use your own virus
          protection software.
        </p>
        <p>
          C. If you become aware of any exploits, bugs or vulnerabilities,
          please let us know at:{" "}
          <a
            href={"mailto:" + supportEmail}
            target="_blank"
            rel="noreferrer noopener"
          >
            {supportEmail}
          </a>
          .
        </p>
        <p>
          D. You must not misuse the Services by knowingly introducing material
          that is malicious or technologically harmful. If you do, your right to
          use the Services will cease immediately.
        </p>
        <h2>13. Can you link to our Interfaces?</h2>
        <p>
          A. You may link to our Interfaces, provided you do so in a way that is
          fair and legal and does not damage our reputation. You must not
          establish a link in such a way as to suggest any form of association,
          approval or endorsement on our part where none exists. You must not
          establish a link to the Services in any application that is not owned
          by or licensed to you.
        </p>
        <p>
          B. Our Interface must not be framed on any other graphical user
          interface, nor may you create a link to any part of our graphical user
          interface other than the home page https://chessdrop.com/. We reserve
          the right to withdraw linking permission without notice.
        </p>
        <p>
          C. The graphical user interface or application in which you are
          linking must comply in all respects with the content standards set out
          in these Terms. If you wish to link to or make any use of content on
          our Interface other than that set out above, please contact:{" "}
          <a
            href={"mailto:" + supportEmail}
            target="_blank"
            rel="noreferrer noopener"
          >
            {supportEmail}.
          </a>
        </p>
        <h2>
          14. What if an event outside our control happens that affects the
          Services?
        </h2>
        <p>
          We may update and change the Services from time to time. We may
          suspend or withdraw or restrict the availability of all or any part of
          the Services for business, operational or regulatory reasons or
          because of a Force Majeure Event at no notice. We shall not be liable
          or responsible to you, or be deemed to have defaulted under or
          breached these Terms, for any failure or delay in the provision of the
          Services or the performance of these Terms, if and to the extent such
          failure or delay is caused by or results from or is connected to acts
          beyond our reasonable control, including the occurrence of a Force
          Majeure Event.
        </p>
        <h2>15. What if part of these Terms are determined to be invalid?</h2>
        <p>
          If any provision of these Terms is held invalid or unenforceable by an
          arbitrator or a court of competent jurisdiction, that provision will
          be enforced to the maximum extent permissible and the other provisions
          of these Terms will remain in full force and effect.
        </p>
        <h2>16. What if we do not enforce certain rights under these Terms?</h2>
        <p>
          The Company’s failure to enforce any right or provision of these Terms
          will not be considered a waiver of such right or provision. The waiver
          of any such right or provision will be effective only if in writing
          and signed by a duly authorized representative of the Company. Except
          as expressly set forth in these Terms, the exercise by either party of
          any of its remedies under these Terms will be without prejudice to its
          other remedies under these Terms or otherwise.
        </p>
        <h2>17. Do third parties have rights?</h2>
        <p>
          Unless it expressly states otherwise, these Terms do not give rise to
          any third party rights, which may be enforced against us.
        </p>
        <h2>18. Can these Terms be assigned?</h2>
        <p>
          You may not assign or transfer these Terms, by operation of law or
          otherwise, without the Company’s prior written consent. Any attempt by
          you to assign or transfer these Terms, without such consent, will be
          null. The Company may freely assign or transfer these Terms without
          restriction. Subject to the foregoing, these Terms will bind and inure
          to the benefit of the parties, their successors and permitted assigns.
        </p>
        <h2>19</h2>
        <p>
          Upon cancellation, suspension, or termination of the Services, the
          provisions that by their nature are intended to survive termination
          will survive including, without limitation, the following sections: 1,
          2, paragraph 3 of section 5, 7-11, and 14-22.
        </p>
        <h2>20. Which laws apply to these Terms?</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of Andorra.
        </p>
        <h2>
          21. How can you get support for the ChessDrop and tell us about any
          problems?
        </h2>
        <p>
          If you want to learn more about the ChessDrop or the Services or have
          any problems using them or have any complaints, please get in touch
          with us via email at:{" "}
          <a
            href={"mailto:" + supportEmail}
            target="_blank"
            rel="noreferrer noopener"
          >
            {supportEmail}
          </a>
          .
        </p>
        <h2>22. How can we resolve disputes?</h2>
        <p>
          A. In the event of a dispute between the parties concerning any matter
          arising from or concerned with these Terms, the parties shall use
          reasonable efforts to settle the dispute through negotiations
          conducted in good faith between the parties.
        </p>
        <p>
          B. Any dispute, controversy or claim arising out of or relating to
          these Terms, or the breach, termination or invalidity thereof, shall
          be settled by arbitration in accordance with the Arbitration
          legislation of Andorra. The number of arbitrators shall be one. The
          place of arbitration shall be determined by the Company, unless agreed
          otherwise. The language to be used in the arbitral proceedings shall
          be English.
        </p>
        <h2>23. User Generated Content</h2>
        <p>
          When users enter games into ChessDrop, the game data may include the
          names of other players. These names may include opponents of the
          user’s own games, or the names of opponents in games in which the
          account owner did not participate. As all game data is under control
          of the user who created it, ChessDrop is not obliged to delete or
          modify any such data on games publicly shared or otherwise made
          available by the controlling user.
        </p>
        <p>
          Notwithstanding the terms specified above, ChessDrop reserves the
          right, at its discretion and for any purpose it deems necessary, to
          modify or delete any User Generated Content.
        </p>
        <h2>24. If you are from the U.S.</h2>
        <p>
          A. You must be 13 (thirteen) years or older to access and use the
          Services. If you are under 13 (thirteen) years of age, you are not
          permitted to access and use the Services. Otherwise you may access to,
          and use of the Services only with the involvement of a parent or
          guardian. Due to the age restrictions for access and use of the
          Services, no information obtained within the Services Provision falls
          within The Children’s Online Privacy Protection Act (“COPPA”) and is
          not monitored as doing so. In any event, you shall not access and use
          the Services, if you are under the age of 13 (thirteen). The COPPA
          requires us to obtain parental consent before we knowingly collect
          personally identifiable information online from children who are under
          the age of 13 (thirteen). We do not knowingly collect or solicit
          personally identifiable information from a child under the age of 13
          (thirteen) without obtaining verifiable consent from that child’s
          parent or guardian (“Parental Consent”), except for the limited amount
          of personally identifiable information we need to collect in order to
          obtain Parental Consent (“Required Information”). Until we have
          received Parental Consent, we will only use Required Information for
          the purpose of obtaining Parental Consent.
        </p>
        <p>
          B. We obtain Parental Consent using methods that are approved under
          the COPPA. For example, we may obtain Parental Consent by asking a
          child’s parent or guardian to sign a consent form that we provide on
          the Infrastructure and to send it back to us via email or electronic
          scan.
        </p>
        <p>
          C. If you are a child under 13 (thirteen), please do not attempt to
          send any personal information about yourself to us before we obtain
          Parental Consent, except for the Required Information in the context
          of the Parental Consent process. If you believe that a child under 13
          (thirteen) has provided us with personal information (beyond the
          Required Information) without our obtaining Parental Consent, please
          contact us at:{" "}
          <a
            href={"mailto:" + supportEmail}
            target="_blank"
            rel="noreferrer noopener"
          >
            {supportEmail}
          </a>
          .
        </p>
        <h2>25. Is this all?</h2>
        <p>
          A. Lakpa SLU and its licensors exclusively own all right, title and
          interest in and to the Services, including all associated intellectual
          property rights. You acknowledge that the Services are protected by
          copyright, trademark, and other laws of Andorra, the United States and
          foreign countries. You agree not to remove, alter or obscure any
          copyright, trademark, service mark or other proprietary rights notices
          incorporated in or accompanying the Services.
        </p>
        <p>
          B. These Terms constitute the entire and exclusive understanding and
          agreement between you and us regarding the Services, and these Terms
          supersede and replace all prior oral or written understandings or
          agreements between you and us regarding the Services.
        </p>
        <p>
          C. Any notices or other communications provided by Lakpa SLU under
          these Terms will be given: (i) via email; or (ii) by posting to the
          Services; or (iii) by posting to social media or other community
          channels. For notices made by email, the date of receipt will be
          deemed the date on which such notice is transmitted.
        </p>
        <p>
          D. If you have any questions about these Terms or the Services, please
          contact the Company at:{" "}
          <a
            href={"mailto:" + supportEmail}
            target="_blank"
            rel="noreferrer noopener"
          >
            {supportEmail}
          </a>
        </p>
      </div>
    </Container>
  </section>
)

export default TermsPage

TermsPage.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>
